import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { ConcreteProposalOP, SlotsResponseOP } from '@cemex-core/cmx-api';

import { ActionTypes, Actions } from '@ordersnproducts/modules/shared/actions/dco.actions';
import {
  OrderBuilderActions,
  RESET_BUILDER,
} from '@ordersnproducts/modules/order-builder/actions/order-builder.actions';
import { RecipeFormValues } from '@ordersnproducts/modules/order-builder/modules/retail-specification/models/form-values';
import { CwcTimeSlot } from '@ordersnproducts/modules/shared/models/dco/cwc-time-slot.model';
import { OrderStatusModify } from '../models/dco/order-status-modify-snapshot.model';
import { WaitingListDeliveryTimeFlexibility } from '../modules/dco/models/waiting-list-delivery-time-flexibility.model';

export interface Slot {
  formId: number;
  formIndex: number;
  requestDatetime: moment.Moment;
  isWaitingList?: boolean;
  waitingListDeliveryTimeFlexibility?: WaitingListDeliveryTimeFlexibility;
  selectedDatetimeSlot?: moment.Moment;
  loading?: boolean;
  loadingScheduleDays?: boolean;
  scheduleDaysError?: any;
  response?: SlotsResponseOP;
  error?: any;
  requestDatetimeIsAvailable?: boolean;
  formSnapshot?: RecipeFormValues;
  timeSlots?: CwcTimeSlot[];
  /**
   * indicates if user accepted the liability check
   */
  liabilityCheck?: boolean;
}
export interface LargeOrderMessage {
  isBigOrder: boolean;
  message: string;
  resetLargeOrder: boolean;
}

/**
 * Out of service hours state
 */
export interface OutOfServiceHours {
  /**
   * determines if the out of service hours is active
   */
  isOutOfServiceHours: boolean;

  /**
   * determines if the user has checked the out of service hours
   */
  check: boolean;
}

export interface State extends EntityState<Slot> {
  selectedSlot: ConcreteProposalOP;
  outOfServiceHours: OutOfServiceHours;
  modifyStatusOrder: OrderStatusModify;
  largeOrder: LargeOrderMessage;
  dcoId: string;
  callDCO: Boolean;
  responseMessage: string;
  openResetWarning: boolean;
  acceptedResetWarning: boolean;
  exceptionMessage: string;
}

export const adapter: EntityAdapter<Slot> = createEntityAdapter<Slot>({
  selectId: (_slot: Slot) => _slot.formId,
  sortComparer: false,
});

const initialState: State = adapter.getInitialState({
  selectedSlot: null,
  outOfServiceHours: {
    isOutOfServiceHours: false,
    check: false,
  },
  modifyStatusOrder: null,
  largeOrder: null,
  dcoId: '--',
  callDCO: null,
  responseMessage: null,
  openResetWarning: false,
  acceptedResetWarning: null,
  exceptionMessage: null,
});

export function reducer(state = initialState, action: Actions | OrderBuilderActions): State {
  switch (action.type) {
    case ActionTypes.SET_REQUEST_DATETIME: {
      if (action.payload.isAvailable != null) {
        return adapter.upsertOne(
          {
            formId: action.payload.formId,
            formIndex: action.payload.formIndex,
            requestDatetime: action.payload.moment,
            requestDatetimeIsAvailable: action.payload.isAvailable,
          },
          state,
        );
      }
      return adapter.upsertOne(
        {
          formId: action.payload.formId,
          formIndex: action.payload.formIndex,
          requestDatetime: action.payload.moment,
        },
        state,
      );
    }
    case ActionTypes.FETCH_SLOTS: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            formIndex: action.payload.formIndex,
            loading: true,
            response: null,
            error: null,
          },
        },
        state,
      );
    }
    case ActionTypes.FETCH_SLOTS_SUCCESS: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            loading: false,
            response: action.payload.response,
            error: null,
            formSnapshot: action.payload.formSnapshot,
          },
        },
        state,
      );
    }
    case ActionTypes.FETCH_SLOTS_ERROR: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            loading: false,
            response: null,
            error: action.payload.error,
            formSnapshot: action.payload.formSnapshot,
          },
        },
        state,
      );
    }
    case ActionTypes.MARK_SLOT_AVAILABLE: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            requestDatetimeIsAvailable: action.payload.isAvailable,
          },
        },
        state,
      );
    }
    case ActionTypes.CHECK_WAITING_LIST: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            isWaitingList: action.payload.checked,
          },
        },
        state,
      );
    }
    case ActionTypes.SET_WAITING_LIST_DELIVERY_TIME_FLEXIBILITY: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            waitingListDeliveryTimeFlexibility: action.payload.waitingListDeliveryTimeFlexibility,
          },
        },
        state,
      );
    }
    case ActionTypes.SELECT_DATETIME_SLOT: {
      if (action.payload.isAvailable != null) {
        return adapter.updateOne(
          {
            id: action.payload.formId,
            changes: {
              selectedDatetimeSlot: action.payload.moment,
              requestDatetimeIsAvailable: action.payload.isAvailable,
            },
          },
          state,
        );
      }
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            selectedDatetimeSlot: action.payload.moment,
          },
        },
        state,
      );
    }
    case ActionTypes.SET_SELECTED_SLOT: {
      return {
        ...state,
        selectedSlot: action.payload,
      };
    }
    case ActionTypes.RESET_SLOTS: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            isWaitingList: null,
            selectedDatetimeSlot: null,
            loading: null,
            loadingScheduleDays: null,
            response: null,
            error: null,
            requestDatetimeIsAvailable: null,
            formSnapshot: null,
            timeSlots: null,
          },
        },
        state,
      );
    }
    case ActionTypes.SAVE_FORM_SNAPSHOT: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            formSnapshot: action.payload.formSnapshot,
          },
        },
        state,
      );
    }
    case ActionTypes.DUMMY_FETCH_SLOTS_SUCCESS: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            formSnapshot: action.payload.formSnapshot,
            loading: false,
            response: { slots: [] },
            error: null,
          },
        },
        state,
      );
    }
    case ActionTypes.FETCH_SCHEDULE_DAYS:
    case ActionTypes.FETCH_SCHEDULE_DAYS_V2: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            loadingScheduleDays: true,
          },
        },
        state,
      );
    }
    case ActionTypes.FETCH_SCHEDULE_DAYS_ERROR: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            loadingScheduleDays: false,
            scheduleDaysError: action.payload.error,
          },
        },
        state,
      );
    }
    case ActionTypes.PUSH_TIME_SLOTS: {
      let aux: Slot;
      for (const id in state.entities) {
        if (Object.prototype.hasOwnProperty.call(state.entities, id)) {
          const element = state.entities[id];
          if (element.formId === action.payload.formId) {
            aux = element;
          }
        }
      }
      let auxArray = [];
      if (aux.timeSlots != null) {
        auxArray = aux.timeSlots;
      }
      action.payload.timeslots.forEach((payloadItem) => {
        const foundIndex = auxArray.findIndex((auxItem) => auxItem.dateMoment.isSame(payloadItem.dateMoment, 'day'));
        if (foundIndex === -1) {
          auxArray.push(payloadItem);
        }
      });
      auxArray.sort((a, b) => a.dateMoment.diff(b.dateMoment));
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            timeSlots: auxArray,
            loadingScheduleDays: false,
          },
        },
        state,
      );
    }
    case ActionTypes.UNSHIFT_TIME_SLOTS: {
      let aux: Slot;
      for (const id in state.entities) {
        if (Object.prototype.hasOwnProperty.call(state.entities, id)) {
          const element = state.entities[id];
          if (element.formId === action.payload.formId) {
            aux = element;
          }
        }
      }
      let auxArray = [];
      if (aux.timeSlots != null) {
        auxArray = aux.timeSlots;
      }
      // recorrer payload al reves, ya estan ordenados ascendentemente
      let i = action.payload.timeslots.length - 1;
      while (i >= 0) {
        const currentItem = action.payload.timeslots[i];
        const foundIndex = auxArray.findIndex((auxItem) => auxItem.dateMoment.isSame(currentItem.dateMoment, 'day'));
        if (foundIndex === -1) {
          auxArray.unshift(currentItem);
        }
        i--;
      }
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            timeSlots: auxArray,
            loadingScheduleDays: false,
          },
        },
        state,
      );
    }
    case ActionTypes.REMOVE_ITEM: {
      return adapter.removeOne(action.payload.formId, state);
    }
    case ActionTypes.DELETE_MODIFY_ORDER_STATUS: {
      return { ...state, modifyStatusOrder: null };
    }
    case ActionTypes.ORDER_MODIFY_HAVE_CHANGES: {
      return { ...state, modifyStatusOrder: { ...state.modifyStatusOrder, haveChanges: action.payload } };
    }
    case ActionTypes.ORDER_MODIFY_COLOR_SPECIAL_SCENARIOS: {
      return { ...state, modifyStatusOrder: { ...state.modifyStatusOrder, colorSpecialScenario: action.payload } };
    }
    case ActionTypes.SET_MODIFY_ORDER_STATUS: {
      return { ...state, modifyStatusOrder: action.payload };
    }
    case ActionTypes.FETCH_SLOTS_BIGO: {
      return {
        ...state,
        largeOrder: { isBigOrder: action.payload.isBigOrder, message: action.payload.message, resetLargeOrder: false },
      };
    }
    case ActionTypes.SAVE_DCO_IDS: {
      return { ...state, dcoId: action.payload };
    }
    case ActionTypes.FETCH_SLOTS_BIGO_RESET: {
      return {
        ...state,
        largeOrder: {
          ...state.largeOrder,
          resetLargeOrder: true,
        },
      };
    }
    case ActionTypes.SAVE_CALL_DCO: {
      return { ...state, callDCO: action.payload };
    }

    case ActionTypes.RESET_CALL_DCO: {
      return { ...state, callDCO: null };
    }

    case ActionTypes.SAVE_SLOTS_OUT_OF_SERVICE: {
      const isOutOfServiceHours = action.payload.active;
      return { ...state, outOfServiceHours: { isOutOfServiceHours, check: false } };
    }

    case ActionTypes.SAVED_SLOTS_OUT_OF_SERVICE_CHECK: {
      const isOutOfServiceHours = state.outOfServiceHours.isOutOfServiceHours;
      return {
        ...state,
        outOfServiceHours: { isOutOfServiceHours, check: isOutOfServiceHours && action.payload.check },
      };
    }

    case ActionTypes.SAVED_SLOTS_OUT_OF_SERVICE_RESET: {
      return { ...state, outOfServiceHours: { isOutOfServiceHours: false, check: false } };
    }

    case ActionTypes.DELETE_DATA:
    case RESET_BUILDER: {
      return initialState;
    }
    case ActionTypes.ACCEPT_LIABILITY_CHECK: {
      return adapter.updateOne(
        {
          id: action.payload.formId,
          changes: {
            liabilityCheck: action.payload.checked,
          },
        },
        state,
      );
    }
    case ActionTypes.REMOVE_ALL_ITEMS_EXCEPT: {
      // ids to delete
      const ids = [];
      for (const id in state.entities) {
        if (+id !== action.payload) {
          ids.push(+id);
        }
      }
      return adapter.removeMany(ids, state);
    }
    case ActionTypes.SAVE_RESPONSE_MESSAGE: {
      return { ...state, responseMessage: action.payload };
    }
    case ActionTypes.RESET_RESPONSE_MESSAGE: {
      return { ...state, responseMessage: null };
    }
    case ActionTypes.TRIGGER_RESET_WARNING_DCO: {
      return { ...state, openResetWarning: action.payload };
    }
    case ActionTypes.ACCEPTED_RESET_WARNING_DCO: {
      return { ...state, acceptedResetWarning: action.payload };
    }
    case ActionTypes.SAVE_EXCEPTION_MESSAGE: {
      return { ...state, exceptionMessage: action.payload };
    }
    case ActionTypes.RESET_EXCEPTION_MESSAGE: {
      return { ...state, exceptionMessage: null };
    }
    default: {
      return state;
    }
  }
}
